import React from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import type { PricingConfiguration, RatePlanType } from '../../../types';

interface PricingModelStepProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
	onNext: () => void;
	onPrevious: () => void;
	hideNavigation?: boolean;
}

const PricingModelStep: React.FC<PricingModelStepProps> = ({
	configuration,
	onUpdate,
	onNext,
	onPrevious,
	hideNavigation = false,
}) => {
	const pricingModels = [
		{
			id: 'IC_PLUS' as RatePlanType,
			name: 'Interchange-Plus',
			description:
				'Pass through wholesale costs (Interchange & Assessments) and add a consistent Discount Rate.',
			recommended: true,
		},
		{
			id: 'TIERED' as RatePlanType,
			name: 'Tiered',
			description:
				'Group transactions into tiers (e.g., Qualified, Non-Qualified) and charge different rates for each.',
			recommended: false,
		},
		{
			id: 'FLAT_TIERED' as RatePlanType,
			name: 'Flat Rate (Transactional)',
			description:
				'Transaction-level statements with flat rates per transaction (e.g., Stripe, Square). Supports both All-Inclusive and Pass-Through pricing modes.',
			recommended: false,
		},
	];

	// Get selected rate types from configuration
	const selectedRateTypes = configuration.selected_rate_types || [];

	const handleModelToggle = (model: RatePlanType) => {
		const currentSelections = selectedRateTypes;
		const newSelections = currentSelections.includes(model)
			? currentSelections.filter((m) => m !== model)
			: [...currentSelections, model];

		// Don't allow empty selection
		if (newSelections.length === 0) return;

		onUpdate({ selected_rate_types: newSelections });
	};

	return (
		<div className="space-y-6">
			<div className="text-center mb-8">
				<h3 className="text-2xl text-gray-900 mb-2">
					What pricing models do you want to configure?
				</h3>
				<p className="text-gray-600">
					Select any combination of pricing models. All selected models will be
					available for file uploads.
				</p>
			</div>

			<div className="space-y-4">
				{pricingModels.map((model) => {
					const isSelected = selectedRateTypes.includes(model.id);
					const isDisabled = false; // Allow any combination, no disabling

					return (
						<div key={model.id} className="relative">
							<label
								className={`flex items-start p-6 border-2 rounded-lg transition-all ${
									isDisabled
										? 'cursor-not-allowed opacity-75'
										: 'cursor-pointer hover:bg-gray-50'
								} ${
									isSelected
										? 'bg-purple-50 border-purple-500'
										: 'bg-white border-gray-200'
								}`}
							>
								<input
									type="checkbox"
									name={`pricing_model_${model.id}`}
									value={model.id}
									checked={isSelected}
									onChange={() => handleModelToggle(model.id)}
									disabled={isDisabled}
									className={`mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded ${
										isDisabled
											? 'opacity-50 cursor-not-allowed'
											: 'cursor-pointer'
									}`}
								/>
								<div className="ml-4 flex-1">
									<div className="flex items-center">
										<span className="text-lg font-semibold text-gray-900">
											{model.name}
										</span>
										{model.recommended && (
											<span className="ml-3 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
												Recommended
											</span>
										)}
									</div>
									<p className="text-gray-600 mt-1">{model.description}</p>
								</div>
							</label>
						</div>
					);
				})}
			</div>

			{/* Navigation - Only show if not hidden */}
			{!hideNavigation && (
				<div className="flex justify-between items-center pt-8 mt-12">
					<button
						onClick={onPrevious}
						className="flex items-center text-gray-500 hover:text-gray-700 transition-colors font-medium"
					>
						<ChevronLeft className="w-5 h-5 mr-1" />
						Previous
					</button>
					<div className="flex items-center text-gray-500">
						<span className="text-sm font-medium">Step 2 of 4</span>
					</div>
					<button
						onClick={onNext}
						disabled={selectedRateTypes.length === 0}
						className={`flex items-center px-8 py-3 rounded-lg transition-colors font-medium shadow-lg ${
							selectedRateTypes.length === 0
								? 'bg-gray-300 text-gray-500 cursor-not-allowed'
								: 'bg-purple-500 hover:bg-purple-600 text-white'
						}`}
					>
						Next
						<ChevronRight className="w-5 h-5 ml-2" />
					</button>
				</div>
			)}
		</div>
	);
};

export default PricingModelStep;
